<script setup>

    import { ref } from 'vue'
    import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
    import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline'

    const props = defineProps({
        title: {
            type: String,
            default: 'Filters'
        }
    })

    const sidebarOpen = ref(false)

</script>

<template>

    <div class="min-h-screen bg-gray-50 lg:flex">

        <TransitionRoot
            as="template"
            :show="sidebarOpen"
        >
            <Dialog
                class="relative z-fullscreen-takeover lg:hidden"
                @close="sidebarOpen = false"
            >
                <TransitionChild
                    as="template"
                    enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="fixed inset-0 bg-lifeworx-blue-700/60 backdrop-blur-sm" />
                </TransitionChild>

                <div class="fixed inset-0 flex">
                    <TransitionChild
                        as="template"
                        enter="transition ease-in-out duration-300 transform"
                        enter-from="-translate-x-full"
                        enter-to="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leave-from="translate-x-0"
                        leave-to="-translate-x-full"
                    >
                        <DialogPanel class="relative mr-12 flex w-full max-w-md flex-1 bg-gray-50 shadow-better">
                            <TransitionChild
                                as="template"
                                enter="ease-in-out duration-300"
                                enter-from="opacity-0"
                                enter-to="opacity-100"
                                leave="ease-in-out duration-300"
                                leave-from="opacity-100"
                                leave-to="opacity-0"
                            >
                                <div class="absolute left-full top-0 flex w-12 justify-center pt-4">
                                    <button
                                        type="button"
                                        class="rounded-md p-2 text-white hover:bg-lifeworx-blue-800/70"
                                        @click="sidebarOpen = false"
                                    >
                                        <span class="sr-only">Close sidebar</span>
                                        <XMarkIcon
                                            class="size-6"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            </TransitionChild>

                            <div class="w-full overflow-y-auto">
                                <slot name="sidebar" />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

        <div class="sticky top-16 z-40 flex items-center gap-x-4 bg-white px-4 py-4 shadow-sm lg:hidden">
            <button
                type="button"
                class="-m-2.5 rounded-md p-2.5 text-lifeworx-blue-700 hover:text-lifeworx-blue-900"
                @click="sidebarOpen = true"
            >
                <span class="sr-only">Open filters</span>
                <Bars3Icon
                    class="size-6"
                    aria-hidden="true"
                />
            </button>
            <div class="flex-1 text-sm font-semibold text-lifeworx-blue-800 truncate">{{ props.title }}</div>
        </div>

        <aside class="fixed top-24 lg:top-16 left-0 hidden shrink-0 lg:block lg:w-[28rem] 2xl:w-[28rem] h-[calc(100vh-4rem)] overflow-y-auto border-r border-stone-200 z-30">
            <slot name="sidebar" />
        </aside>

        <main class="min-h-screen w-full min-w-0 lg:ml-[28rem] 2xl:ml-[28rem]">
            <slot />
        </main>

    </div>

</template>
